import React, { Suspense, useState } from 'react'
import { ErrorMessage, Formik, Form } from 'formik'
import SimpleBar from 'simplebar-react'
import { CSSTransition } from 'react-transition-group'
import { Nav, Button, Navbar, Modal, Form as BForm, Spinner } from '@themesberg/react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { getItem } from '../../utils/storageUtils'
import PlatformLogo from '../../../src/assets/img/brand/AMG_Platform_Logo_no_bg.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faEdit } from '@fortawesome/pro-solid-svg-icons'
import Trigger from '../OverlayTrigger'
import { isTenant } from '../../utils/constants'
import { backOfficeLogoSchema } from './schema/backOfficeLogoSchema'
import { boLogoUpdateStart } from '../../store/redux-slices/admins'
import './styles/Sidebar.scss'

const SuperAdminNavItems = React.lazy(() => import('./components/SuperAdminNavItems'))
const TenantNavItems = React.lazy(() => import('./components/TenantNavItems'))

export default (props = {}) => {
  const [show, setShow] = useState(false)
  const [openLogoUpdateModal, setOpenLogoUpdateModal] = useState(false)
  const dispatch = useDispatch()
  const showClass = show ? 'show' : ''

  let { role, adminRole, tenantAdminRole } = useSelector((state) => state.login)
  const { adminRoleId, superAdminUser, adminDetails, boLogoLoader, boLogo } = useSelector(state => state.admins)

  if (!role) {
    role = getItem('role')
  }

  const roleData =
    adminRole
      ? adminRole?.find(val => val.superRoleId !== 1 && val.superRoleId === parseInt(adminRoleId))?.name
      : tenantAdminRole?.find(val => val.adminRoleId !== 1 && val.adminRoleId === parseInt(superAdminUser?.adminRoleId))?.name

  const onCollapse = () => setShow(!show)

  return (
    <>
      <Navbar
        expand={false}
        collapseOnSelect
        variant='dark'
        className='navbar-theme-primary p-2 d-md-none'
      >
        <div className='d-flex align-items-center justify-content-between w-100'>
          {/* Logo in header - visible in small devices */}
          <img style={{ maxWidth: '70px', height: '45px' }} src={boLogo || PlatformLogo} />
          <Navbar.Toggle
            as={Button}
            aria-controls='main-navbar'
            onClick={onCollapse}
          >
            <span className='navbar-toggler-icon' />
          </Navbar.Toggle>
        </div>

      </Navbar>
      <CSSTransition timeout={300} in={show} classNames='sidebar-transition'>
        <SimpleBar className={`collapse ${showClass} sidebar d-md-block bg-primary text-white pb-4`}>
          <Navbar.Toggle
            as={Button}
            aria-controls='main-navbar'
            onClick={onCollapse}
            className='navbar-close'
          >
            <FontAwesomeIcon icon={faXmark} />
          </Navbar.Toggle>
          <div className='sidebar-inner px-4 pt-3'>
            <Nav className='flex-column pt-3 pt-md-0'>
              <div className='logo-container text-center mb-3'>
                <img style={{ maxWidth: '100px', height: '60px', marginRight: '20px' }} src={boLogo || PlatformLogo} />

                <Trigger message='Update logo'>
                  <Button
                    size='sm'
                    className='edit-btn'
                    variant='outline-secondary'
                    hidden={!isTenant || (isTenant && adminDetails?.adminRoleId !== 1)}
                    onClick={() => setOpenLogoUpdateModal(true)}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                </Trigger>
              </div>
              <div className='d-flex flex-column w-100'>
                <h5 className='d-flex m-auto mb-0 align-items-center'>
                  {role}&nbsp;
                  {roleData && <span style={{ fontSize: '15px' }}>({roleData})</span>}
                </h5>
                {(superAdminUser?.email || adminDetails?.email) &&
                  <Trigger message={superAdminUser?.email || adminDetails?.email}>
                    <span
                      style={{
                        fontSize: '15px',
                        textAlign: 'center',
                        whitespace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}
                    >
                      {superAdminUser?.email || adminDetails?.email}
                    </span>
                  </Trigger>}
              </div>
              <hr />

              {role === 'Super Admin' && (
                <Suspense fallback={null}>
                  <SuperAdminNavItems setShow={setShow} />
                </Suspense>
              )}

              {role === 'Admin' &&
                <Suspense fallback={null}>
                  <TenantNavItems setShow={setShow} />
                </Suspense>}
            </Nav>
          </div>
        </SimpleBar>
      </CSSTransition>

      {openLogoUpdateModal && (
        <Modal size='sm' show={openLogoUpdateModal}>
          <Modal.Header className='justify-content-center'>
            <h4>Upload New Logo</h4>
          </Modal.Header>

          <Formik
            initialValues={{
              logo: null
            }}
            validationSchema={backOfficeLogoSchema}
            onSubmit={(formValues) => {
              dispatch(boLogoUpdateStart({ logo: formValues?.logo, setModalVisible: setOpenLogoUpdateModal }))
            }}
          >
            {({ setFieldValue, handleBlur, handleSubmit }) => (
              <Form className='align-items-center mt-3'>
                <Modal.Body>
                  <BForm.Control
                    type='file'
                    name='logo'
                    onChange={(event) => setFieldValue('logo', event.target.files[0])}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage
                    component='div'
                    name='logo'
                    className='text-danger'
                  />

                  <div className='mt-4 d-flex justify-content-between align-items-center'>
                    <Button
                      variant='outline-warning'
                      onClick={() => setOpenLogoUpdateModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='outline-success'
                      onClick={() => {
                        handleSubmit()
                      }}
                      className='ml-2'
                    >
                      Submit

                      {boLogoLoader && (
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                        />
                      )}
                    </Button>
                  </div>
                </Modal.Body>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
    </>
  )
}
